import Avatar, { Action, Frame } from './Avatar'

interface ActionState {
  readonly name: string
  readonly angle: number
  readonly speed: number
  /// Total duration of frames of current angle
  readonly totalDuration: number
  readonly angleIndex: number
}

export default class AvatarController {
  readonly avatar: Avatar

  private state: ActionState
  private _frameIndex: number
  private _remainder: number

  get actionName (): string {
    return this.state.name
  }

  get action (): Action {
    return this.avatar.actions.get(this.state.name)!
  }

  get angle (): number {
    return this.state.angle
  }

  get speed (): number {
    return this.state.speed
  }

  get frameIndex () {
    return this._frameIndex
  }

  get frame (): Frame {
    return this.action.frames[this.state.angleIndex][this.frameIndex]
  }

  constructor ({
    avatar,
    actionName = 'stand',
    angle = 0,
    speed = 1
  }: {
    avatar: Avatar
    actionName?: string
    angle?: number
    speed?: number
  }) {
    this.avatar = avatar
    const angleIndex = avatar.indexForAngle(angle)
    this.state = {
      name: actionName,
      totalDuration: this.totalDuration(actionName, angleIndex),
      angle,
      speed,
      angleIndex
    }
    this._frameIndex = 0
    this._remainder = 0
  }

  act (actionName: string, angle: number, speed: number) {
    const action = this.avatar.actions.get(actionName)
    if (!action) {
      return
    }
    const newActionIndex = this.avatar.indexForAngle(angle)

    // only interrupt the animation if we're not doing the same action and
    // toward the same direction
    if (
      this.state.name !== actionName ||
      this.state.angleIndex !== newActionIndex
    ) {
      this._frameIndex = 0
      this._remainder = 0
    }

    const angleIndex = this.avatar.indexForAngle(angle)
    this.state = {
      angle,
      speed,
      angleIndex,
      name: actionName,
      totalDuration: this.totalDuration(actionName, angleIndex)
    }
  }

  updateFrame (elasped: number) {
    const action = this.avatar.actions.get(this.state.name)!
    const frames = action.frames[this.state.angleIndex]

    this._remainder += elasped * this.state.speed
    // the remainder for the total duration, in case time delta is too big
    this._remainder %= this.state.totalDuration

    while (this._remainder >= frames[this.frameIndex].delay) {
      this._remainder -= frames[this.frameIndex].delay
      this._frameIndex += 1
      if (this._frameIndex >= frames.length) {
        this._frameIndex = 0
      }
    }
  }

  // get total duration of a action in given angle
  private totalDuration (actionName: string, angleIndex: number) {
    const action = this.avatar.actions.get(actionName)!
    return action.frames[angleIndex].reduce(
      (previous, frame) => previous + frame.delay,
      0
    )
  }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/models/MapObjects/AvatarController.ts